<template>
    <b-modal
        id="modal-create-survey"
        ref="modal-create-survey"
        centered
        ok-title="Aceptar"
        ok-variant="primary"
        cancel-title="Cancelar"
        :title="modalTitle"
        :ok-disabled="disableOkButton"
        @ok="onCreateUpdate"
        @hidden="resetForm()"
    >
        <b-form id="survey-form" @submit.stop.prevent="onSubmit()">

            <b-form-group
                id="input-group-survey-name"
                v-if="selectedCrudObject === null || (selectedCrudObject && selectedCrudObject.state === 'draft')"
                label="*Nombre:"
                label-for="input-survey-name"
            >
                <b-form-input
                    id="input-survey-name"
                    v-model.trim="$v.form.name.$model"
                    name="survey-name"
                    type="text"
                    required
                    :state="validateState('name')"
                    :disabled="formDisabled"
                />
                <b-form-invalid-feedback id="input-survey-name-feedback">
                    <ul>
                        <li v-if="!$v.form.name.required">Este campo es requerido.</li>
                    </ul>
                </b-form-invalid-feedback>
            </b-form-group>

            <b-form-group
                id="input-group-survey-description"
                v-if="selectedCrudObject === null || (selectedCrudObject && selectedCrudObject.state === 'draft')"
                label="Descripción:"
                label-for="input-survey-description"
            >
                <b-form-textarea
                    id="input-survey-description"
                    v-model.trim="form.description"
                    name="survey-description"
                    rows="4"
                    no-resize
                    :disabled="formDisabled"
                />
            </b-form-group>

            <b-form-group
                id="input-group-survey-durationMinutes"
                v-if="selectedCrudObject === null || (selectedCrudObject && selectedCrudObject.state === 'draft')"
                label="*Duración(minutos):"
                label-for="input-survey-durationMinutes"
                description="Indica cuantos minutos durará una encuesta/quiz antes de automaticamente completarla desde que el alumno la comienza. El 0 representa duración infinita."
            >
                <b-form-input
                    id="input-survey-durationMinutes"
                    v-model.trim="$v.form.durationMinutes.$model"
                    name="survey-durationMinutes"
                    type="number"
                    min="0"
                    step="10"
                    number
                    required
                    :state="validateState('durationMinutes')"
                    :disabled="formDisabled"
                />
                <b-form-invalid-feedback id="input-survey-durationMinutes-feedback">
                    <ul>
                        <li v-if="!$v.form.durationMinutes.required">Este campo es requerido.</li>
                        <li v-if="!$v.form.durationMinutes.minValue">Debe ser mayor o igual a 0.</li>
                        <li v-if="!$v.form.durationMinutes.integer">Solo se aceptan números enteros.</li>
                    </ul>
                </b-form-invalid-feedback>
            </b-form-group>

            <b-form-group
                id="input-group-survey-isEvaluated"
                v-if="selectedCrudObject === null || (selectedCrudObject && selectedCrudObject.state === 'draft')"
            >
                <b-form-checkbox
                    id="input-survey-isEvaluated"
                    v-model="form.isEvaluated"
                    name="survey-isEvaluated"
                    size="lg"
                    switch
                    :disabled="formDisabled"
                >
                    ¿Evaluar como Quiz?
                </b-form-checkbox>
            </b-form-group>

            <b-form-group
                id="input-group-survey-showAnswers"
                v-if="form.isEvaluated"
                description="Solo se indicará si las preguntas fueron respondidas correctamente o no. No se indicaran cuales son las opciones correctas."
            >
                <b-form-checkbox
                    id="input-survey-showAnswers"
                    v-model="form.showAnswers"
                    name="survey-showAnswers"
                    size="lg"
                    switch
                    :disabled="formDisabled"
                >
                    ¿Desplegar respuestas incorrectas al completar?
                </b-form-checkbox>
            </b-form-group>

        </b-form>
    </b-modal>
</template>

<script>
import { required, minValue, integer } from 'vuelidate/lib/validators'
import { mapState, mapGetters, mapActions } from 'vuex'

export default {
    name: 'SurveyCreateUpdateModal',
    data () {
        return {
            formDisabled: false,
            modalTitle: 'Agregar Nueva Encuesta/Quiz',
            form: {
                surveyId: null,
                name: '',
                description: '',
                isEvaluated: false,
                showAnswers: false,
                durationMinutes: 0
            },
            oldForm: {
                name: '',
                description: '',
                isEvaluated: false,
                showAnswers: false,
                durationMinutes: 0
            }
        }
    },
    validations: {
        form: {
            name: {
                required
            },
            durationMinutes: {
                required,
                minValue: minValue(0),
                integer
            }
        }
    },
    computed: {
        ...mapState([
            'activeSite',
            'selectedCrudObject'
        ]),
        ...mapGetters([
            'getActiveSiteIdentifiers'
        ]),
        disableOkButton () {
            if (this.selectedCrudObject) {
                return this.formDisabled || this.$v.form.$invalid ||
                (this.form.name === this.oldForm.name &&
                this.form.description === this.oldForm.description &&
                this.form.isEvaluated === this.oldForm.isEvaluated &&
                this.form.showAnswers === this.oldForm.showAnswers &&
                this.form.durationMinutes === this.oldForm.durationMinutes)
            } else {
                return this.formDisabled || this.$v.form.$invalid
            }
        }
    },
    watch: {
        selectedCrudObject (val, oldVal) {
            // If selectedCrudObject is given a value then the form enters Update mode
            // otherwise the form will always be in Create mode
            if (val) {
                this.modalTitle = 'Editar Encuesta/Quiz'
                this.form.surveyId = this.selectedCrudObject.id
                this.form.name = this.oldForm.name = this.selectedCrudObject.name
                this.form.description = this.oldForm.description = this.selectedCrudObject.description
                this.form.isEvaluated = this.oldForm.isEvaluated = this.selectedCrudObject.isEvaluated
                this.form.showAnswers = this.oldForm.showAnswers = this.selectedCrudObject.showAnswers
                this.form.durationMinutes = this.oldForm.durationMinutes = this.selectedCrudObject.durationMinutes
            } else {
                this.resetForm()
            }
        }
    },
    methods: {
        ...mapActions([
            'createSiteSurvey',
            'updateSiteSurvey',
            'readSingleSiteTeacherSurvey',
            'readSiteTeacherSurveys'
        ]),
        validateState (name) {
            const { $dirty, $error } = this.$v.form[name]
            return $dirty ? !$error : null
        },
        async onCreateUpdate (bvModalEvt) {
            bvModalEvt.preventDefault() // Prevent modal from closing
            await this.onSubmit() // Trigger submit handler
        },
        resetForm () {
            this.modalTitle = 'Agregar Nueva Encuesta/Quiz'
            this.form.surveyId = null
            this.form.name = this.oldForm.name = ''
            this.form.description = this.oldForm.description = ''
            this.form.isEvaluated = this.oldForm.isEvaluated = false
            this.form.showAnswers = this.oldForm.showAnswers = false
            this.form.durationMinutes = this.oldForm.durationMinutes = 0
            this.$v.$reset()
            this.$store.commit('RESET_SELECTED_CRUD_OBJECT')
        },
        async onSubmit () {
            let loader = this.$root.$loading.show()
            try {
                this.formDisabled = true
                const payloadData = { ...this.form, courseSiteId: this.activeSite.id }
                if (this.selectedCrudObject) {
                    // Update mode
                    await this.updateSiteSurvey(payloadData)
                    var reloadPayload = { courseSiteId: this.activeSite.id, surveyId: this.selectedCrudObject.id }
                    await this.readSingleSiteTeacherSurvey(reloadPayload)
                    this.$root.infoToast(`Encuesta/Quiz editada.`)
                } else {
                    // Create mode
                    await this.createSiteSurvey(payloadData)
                    // Reload the current active site surveys
                    await this.readSiteTeacherSurveys(this.activeSite.id)
                    this.$root.successToast(`Encuesta/Quiz creada exitosamente.`)
                }
                this.$nextTick(() => {
                    this.$bvModal.hide('modal-create-survey')
                })
                this.resetForm()
            } catch (err) {
                console.log({ err: err })
                if (err.response && (err.response.status === 404 || err.response.status === 400)) {
                    this.$root.dangerToast('Datos incorrectos, vuelva a intentar.')
                } else {
                    this.$root.unknownErrorToast()
                }
            } finally {
                this.formDisabled = false
                loader.hide()
            }
        }
    }
}
</script>
